import React, { useEffect, useMemo, useState } from "react";
import {
  Box, Button, Heading, Input, Select, VStack, Text, Switch, FormControl,
  FormLabel, FormHelperText, useToast, Image, HStack, Textarea, Divider,
  Collapse, IconButton, Tooltip, Table, Thead, Tbody, Tr, Th, Td, Tag,
  Badge, SimpleGrid, Menu, MenuButton, MenuList, MenuItem, Spacer, Skeleton
} from "@chakra-ui/react";
import { ChevronDownIcon, ChevronUpIcon, InfoOutlineIcon, RepeatIcon } from "@chakra-ui/icons";
import Papa from "papaparse";
import * as XLSX from "xlsx";
import AppLayout from "../components/layout/AppLayout";

const Charts = () => {
  const BRAND_GREEN = "#1C4C3F";     // dark green you’re using elsewhere
  const OFF_WHITE = "#F7F5EB";       // cream/off-white

  const [mode, setMode] = useState("form"); // "form" | "prompt"

  // Shared
  const [file, setFile] = useState(null);
  const [headers, setHeaders] = useState([]);
  const [rows, setRows] = useState([]); // full parsed rows for insights
  const [dataPreview, setDataPreview] = useState([]);
  const [chartUrl, setChartUrl] = useState("");
  const [resolvedConfig, setResolvedConfig] = useState(null);
  const [showConfig, setShowConfig] = useState(false);
  const [loadingChart, setLoadingChart] = useState(false);

  // Form mode state
  const [xCol, setXCol] = useState("");
  const [yCol, setYCol] = useState("");
  const [groupCol, setGroupCol] = useState("");
  const [yCols, setYCols] = useState([]);
  const [chartType, setChartType] = useState("bar");
  const [chartTitle, setChartTitle] = useState("My Chart");
  const [yAxisFormat, setYAxisFormat] = useState("");
  const [showAnnotations, setShowAnnotations] = useState(false);

  // Prompt mode state
  const [promptText, setPromptText] = useState("");

  // Insights
  const [insights, setInsights] = useState([]);

  const toast = useToast();
  const API_BASE = process.env.REACT_APP_API_BASE_URL;

  const chartTypes = [
    "bar","horizontal_bar","stacked_component_bar","stacked_component_bar_horizontal",
    "line","line_dual","pie","grouped_bar",
  ];

  // ✅ NEW: Reset chart config whenever a NEW FILE (dataset) is selected
  useEffect(() => {
    // When a new dataset is uploaded, old column selections are invalid.
    setXCol("");
    setYCol("");
    setYCols([]);
    setGroupCol("");
    setYAxisFormat("");
    setShowAnnotations(false);

    // Optional UX resets (kept minimal / safe)
    setChartType("bar");
    // setChartTitle("My Chart"); // leave title as-is to avoid unexpected UX changes
  }, [file]);

  // ✅ NEW: Clear incompatible fields when chart type changes (prevents stale config across types)
  useEffect(() => {
    // dual/stacked uses yCols; single-series uses yCol
    if (chartType === "line_dual" || chartType.includes("stacked_component")) {
      setYCol("");
    } else {
      setYCols([]);
    }

    // grouped_bar uses groupCol; other types should clear it
    if (chartType !== "grouped_bar") {
      setGroupCol("");
    }
  }, [chartType]);

  // ---------- Excel date helpers (NEW) ----------
  // Excel serial date (days since 1899-12-30) -> JS Date
  function excelSerialToDate(serial) {
    const excelEpoch = new Date(Date.UTC(1899, 11, 30)); // 1899-12-30
    const msPerDay = 24 * 60 * 60 * 1000;
    return new Date(excelEpoch.getTime() + serial * msPerDay);
  }

  // Format as M/D/YY for display
  function formatShortDate(d) {
    const m = d.getUTCMonth() + 1;
    const day = d.getUTCDate();
    const yy = d.getUTCFullYear().toString().slice(-2);
    return `${m}/${day}/${yy}`;
  }

  // Convert numeric Excel-serial date columns into short date strings
  function normalizeExcelDateColumns(inputRows) {
    if (!inputRows || inputRows.length === 0) return inputRows;

    const SERIAL_MIN = 40000; // ~2009
    const SERIAL_MAX = 60000; // ~2064

    const cols = Object.keys(inputRows[0] || {});

    // column names that *sound* like dates
    const dateLikeCols = cols.filter((col) =>
      /(date|week|period|month|year|time)/i.test(col)
    );

    const numericDateCols = dateLikeCols.filter((col) => {
      const vals = inputRows
        .map((r) => r[col])
        .filter((v) => v !== null && v !== undefined && v !== "");

      if (!vals.length) return false;

      const numericVals = vals.filter((v) => typeof v === "number");
      if (!numericVals.length) return false;

      const inRangeCount = numericVals.filter(
        (v) => v >= SERIAL_MIN && v <= SERIAL_MAX
      ).length;

      return inRangeCount / numericVals.length >= 0.8;
    });

    if (!numericDateCols.length) return inputRows;

    return inputRows.map((row) => {
      const copy = { ...row };
      numericDateCols.forEach((col) => {
        const val = row[col];
        if (typeof val === "number") {
          const d = excelSerialToDate(val);
          copy[col] = formatShortDate(d); // keep as formatted string for preview + insights
        }
      });
      return copy;
    });
  }

  // ---------- Helpers to classify columns ----------
  const isNumericCol = (sampleRows, key) => {
    let seen = 0, nums = 0;
    for (let i = 0; i < Math.min(sampleRows.length, 100); i++) {
      const v = sampleRows[i]?.[key];
      if (v === undefined || v === null || v === "") continue;
      seen++;
      const s = String(v).replace(/[$,%\s,]/g, "");
      if (!isNaN(Number(s))) nums++;
    }
    return seen > 0 && nums / seen >= 0.7;
  };

  const looksLikeDate = (val) => {
    if (!val) return false;
    const s = String(val).trim();
    return (
      /^\d{4}[-/]\d{1,2}([-/]\d{1,2})?$/.test(s) ||
      /^\d{1,2}\/\d{1,2}\/\d{2,4}$/.test(s) ||
      /^(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)/i.test(s)
    );
  };

  const isTimeCol = (sampleRows, key) => {
    let seen = 0, matches = 0;
    for (let i = 0; i < Math.min(sampleRows.length, 100); i++) {
      const v = sampleRows[i]?.[key];
      if (v === undefined || v === null || v === "") continue;
      seen++;
      if (looksLikeDate(v)) matches++;
    }
    return seen > 0 && matches / seen >= 0.6;
  };

  const numericHeaders = useMemo(
    () => headers.filter((h) => isNumericCol(rows, h)),
    [headers, rows]
  );

  const autoGuess = (sampleRows) => {
    if (!sampleRows?.length || !headers?.length) return;
    let guessX = headers.find((h) => isTimeCol(sampleRows, h));
    if (!guessX) guessX = headers.find((h) => !isNumericCol(sampleRows, h)) || headers[0];

    let guessY = headers.find((h) => h !== guessX && isNumericCol(sampleRows, h));
    const otherNumerics = headers.filter((h) => h !== guessX && isNumericCol(sampleRows, h));
    const guessY2 = otherNumerics[1];
    const guessGroup = headers.find((h) => h !== guessX && !isNumericCol(sampleRows, h));

    let type = "bar";
       if (isTimeCol(sampleRows, guessX) && guessY && guessY2) type = "line_dual";
    else if (isTimeCol(sampleRows, guessX) && guessY) type = "line";
    else if (guessGroup) type = "grouped_bar";

    setXCol(guessX || "");
    setYCol(guessY || "");
    setYCols(guessY2 ? [guessY, guessY2] : (guessY ? [guessY] : []));
    setGroupCol(type === "grouped_bar" ? (guessGroup || "") : "");
    setChartType(type);
  };

  // ---------- File parsing (CSV/XLSX) ----------
  const parseCSV = (f) =>
    new Promise((resolve, reject) => {
      Papa.parse(f, {
        header: true,
        dynamicTyping: false,
        skipEmptyLines: true,
        complete: (res) => resolve(res.data),
        error: reject,
      });
    });

  const parseXLSX = async (f) => {
    const ab = await f.arrayBuffer();
    const wb = XLSX.read(ab);
    const ws = wb.Sheets[wb.SheetNames[0]];
    return XLSX.utils.sheet_to_json(ws, { defval: "" });
  };

  const handleFileChange = async (e) => {
    const uploaded = e.target.files[0];
    setFile(uploaded);
    setHeaders([]);
    setDataPreview([]);
    setRows([]);
    setInsights([]);
    setChartUrl("");
    setResolvedConfig(null);

    if (!uploaded) return;

    try {
      let parsed = [];
      if (uploaded.name.toLowerCase().endsWith(".csv")) {
        parsed = await parseCSV(uploaded);
      } else if (uploaded.name.toLowerCase().endsWith(".xlsx")) {
        parsed = await parseXLSX(uploaded);
      } else {
        toast({ title: "Unsupported file", status: "warning" });
        return;
      }

      // 🔹 NEW: normalize Excel serial date columns
      parsed = normalizeExcelDateColumns(parsed);

      const keys = parsed.length ? Object.keys(parsed[0]) : [];
      setHeaders(keys);
      setRows(parsed); // store full rows for insights
      setDataPreview(parsed.slice(0, 5));
      autoGuess(parsed);
      toast({ title: "File loaded", description: "Columns detected", status: "success", duration: 1500 });
    } catch (err) {
      toast({ title: "Failed to parse file", description: err.message, status: "error" });
    }
  };

  // ===== Existing form-based flow (unchanged backend) =====
  const handleChartGeneration = async (override = {}) => {
    if (!file || !(override.xCol ?? xCol) || !chartTitle) {
      toast({
        title: "Missing Required Fields",
        description: "Please upload a file, set X axis, and chart title.",
        status: "warning",
      });
      return;
    }

    const _x = override.xCol ?? xCol;
    const _y = override.yCol ?? yCol;
    const _ys = override.yCols ?? yCols;
    const _group = override.groupCol ?? groupCol;
    const _type = override.chartType ?? chartType;
    const _fmt = override.yAxisFormat ?? yAxisFormat;
    const _annot = override.showAnnotations ?? showAnnotations;

    // ✅ NEW: Guard against stale column selections after switching datasets
    const missing = [];
    if (_x && !headers.includes(_x)) missing.push(_x);
    if (_y && !headers.includes(_y)) missing.push(_y);
    if (_group && !headers.includes(_group)) missing.push(_group);
    if (_ys && _ys.length) {
      _ys.forEach((c) => {
        if (c && !headers.includes(c)) missing.push(c);
      });
    }
    if (missing.length) {
      toast({
        title: "Chart config reset needed",
        description: `These columns aren't in the current dataset: ${missing.join(", ")}. Please re-select fields and try again.`,
        status: "warning",
      });
      return;
    }

    const formData = new FormData();
    formData.append("file", file);
    formData.append("x_col", _x);
    formData.append("chart_title", chartTitle);
    formData.append("chart_type", _type);
    if (_y) formData.append("y_col", _y);
    if (_group) formData.append("group_col", _group);
    if (_ys && _ys.length > 0) formData.append("y_cols", _ys.join(","));
    if (_fmt) formData.append("y_axis_format", _fmt);
    formData.append("show_annotations", String(_annot));

    try {
      setLoadingChart(true);
      const res = await fetch(`${API_BASE}/api/generate-chart-from-file/`, {
        method: "POST",
        body: formData,
      });
      const data = await res.json();

      if (data.error) {
        toast({ title: "Chart Generation Error", description: data.error, status: "error" });
      } else {
        setChartUrl(data.chart_url);
        setResolvedConfig(data.config || null);
        toast({ title: "Chart generated!", status: "success" });
        // Generate insights client-side using rows + config used
        computeInsights(rows, { xCol: _x, yCol: _y, yCols: _ys, groupCol: _group, chartType: _type, yAxisFormat: _fmt });
      }
    } catch (err) {
      toast({ title: "Upload Failed", description: err.message, status: "error" });
    } finally {
      setLoadingChart(false);
    }
  };

  // ===== Natural-language flow (kept) =====
  const handlePromptGeneration = async () => {
    if (!file || !promptText.trim()) {
      toast({
        title: "Missing Required Fields",
        description: "Please upload a file and enter a prompt.",
        status: "warning",
      });
      return;
    }


    const formData = new FormData();
    formData.append("file", file);
    formData.append("prompt", promptText);

    if (chartTitle) {
       formData.append("chart_title", chartTitle);
    }

    try {
      setLoadingChart(true);
      const res = await fetch(`${API_BASE}/api/charts/generate-from-text`, {
        method: "POST",
        body: formData,
      });
      const data = await res.json();
      if (data.error) {
        toast({
          title: "Chart Generation Error",
          description: data.error,
          status: "error",
        });
      } else {
        setChartUrl(data.chart_url);
        setResolvedConfig(data.config || null);
        toast({ title: "Chart generated from prompt!", status: "success" });

        // Insights from inferred config
        const cfg = data?.config || {};
        computeInsights(rows, {
          xCol: cfg.x_col,
          yCol: cfg.y_col,
          yCols: cfg.y_cols,
          groupCol: cfg.group_col,
          chartType: cfg.chart_type,
          yAxisFormat: cfg.y_axis_format,
        });
      }
    } catch (err) {
      toast({ title: "Request Failed", description: err.message, status: "error" });
    } finally {
      setLoadingChart(false);
    }
  };

  // ---------- Insights generator (FIXED aggregation rules) ----------
  function cleanNumber(v) {
    if (v === undefined || v === null || v === "") return null;
    const s = String(v).replace(/[$,%\s,]/g, "");
    const n = Number(s);
    return isNaN(n) ? null : n;
  }

  // metric key normalizer
  function mkey(name) {
    return (name || "").toString().trim().toLowerCase().replace(/\s+/g, "");
  }

  // pick aggregation rule consistent with backend
  function pickAgg(metricName) {
    const k = mkey(metricName);
    if (k.includes("acv")) return "max";
    if (k.includes("velocity") || k.includes("vel")) return "mean";
    if (k.includes("dollar") || k.includes("revenue") || k.includes("sales")) return "sum";
    if (k.includes("unit")) return "sum";
    return "mean";
  }

  // group rows by key and aggregate a metric using rule
  function groupAggregate(allRows, dim, metric) {
    const rule = pickAgg(metric);
    const buckets = new Map(); // key -> array of values
    for (const r of allRows) {
      const key = r?.[dim];
      const raw = r?.[metric];
      const v = cleanNumber(raw);
      if (key === undefined || v === null) continue;
      if (!buckets.has(key)) buckets.set(key, []);
      buckets.get(key).push(v);
    }
    const out = [];
    buckets.forEach((arr, key) => {
      if (!arr.length) return;
      let val;
      if (rule === "sum") val = arr.reduce((a, b) => a + b, 0);
      else if (rule === "mean") val = arr.reduce((a, b) => a + b, 0) / arr.length;
      else if (rule === "max") val = Math.max(...arr);
      else val = arr.reduce((a, b) => a + b, 0) / arr.length;
      out.push({ k: key, v: val });
    });
    out.sort((a, b) => b.v - a.v);
    return out;
  }

  // Smart percent formatting:
  // - If value is 0–1 (fraction), multiply by 100
  // - If value is already 1–100 (percent-like), just append %
  function formatPercentSmart(v) {
    const n = Number(v);
    if (!isFinite(n)) return String(v);

    // Treat values up to ~1.2 as fractional (e.g., 0.36 → 36%)
    if (Math.abs(n) <= 1.2) {
      // show 0 decimals when >=10%, otherwise 1 decimal for nicer small percents
      const pct = n * 100;
      const dp = Math.abs(pct) < 10 ? 1 : 0;
      return `${pct.toFixed(dp)}%`;
    }

    // Already percent-like (e.g., 36, 88) → append %
    return `${n.toFixed(0)}%`;
  }

  function formatValue(v, fmt, metric /* optional */) {
    if (v === null || v === undefined) return String(v);
    const k = mkey(metric || "");

    // Velocity always with 2 decimals
    if (k.includes("velocity") || k.includes("usw"))
      return Number(v).toFixed(1);

    // Percent display with smart scaling
    if (fmt === "%") return formatPercentSmart(v);

    // Dollar formatting
    if (fmt === "$") return `$${Number(v).toLocaleString()}`;

    // Default numeric formatting
    return Number(v).toLocaleString();
  }

  function computeInsights(allRows, cfg) {
    try {
      if (!allRows?.length || !(cfg?.xCol)) {
        setInsights([]);
        return;
      }
      const nums = (cfg.yCols && cfg.yCols.length > 0) ? cfg.yCols : (cfg.yCol ? [cfg.yCol] : []);
      if (!nums.length) {
        setInsights([]);
        return;
      }

      const ins = [];
      const xIsTime = allRows.some(r => looksLikeDate(r[cfg.xCol]));
      const considerCat = !xIsTime;

      // 1) Top categories & gap by selected X
      nums.forEach(metric => {
        if (!metric) return;
        const arr = groupAggregate(allRows, cfg.xCol, metric); // uses correct rule
        if (!arr.length) return;

        const top = arr[0];
        const second = arr[1];

        // only treat X as "category" when it's NOT a date/time column
        if (considerCat && top) {
          ins.push(`• Top ${cfg.xCol} by ${metric}: ${top.k} (${formatValue(top.v, cfg.yAxisFormat, metric)}).`);
        }
        if (second && top) {
          const gap = second.v !== 0 ? ((top.v - second.v) / second.v) * 100 : 0;
          ins.push(`• ${metric}: ${top.k} leads ${second.k} by ~${gap.toFixed(0)}%.`);
        }
      });

      // 2) Trend direction if X is time (first metric only; mean across same date if needed)
      if (xIsTime && nums[0]) {
        const metric = nums[0];
        const dated = groupAggregate(allRows, cfg.xCol, metric)  // aggregated per time key using proper rule
          .filter(p => looksLikeDate(p.k))
          .sort((a, b) => new Date(a.k) - new Date(b.k));
        if (dated.length >= 3) {
          const first = dated[0].v;
          const last = dated[dated.length - 1].v;
          if (first !== null && last !== null && first !== 0) {
            const chg = ((last - first) / Math.abs(first)) * 100;
            const dir = chg >= 0 ? "up" : "down";
            ins.push(`• ${metric} trend: ${dir} ~${Math.abs(chg).toFixed(0)}% from start to end.`);
          }
        }
      }

      // 3) If you have a Region column, add a region comparison for the first metric
      if (headers.includes("Region") && nums[0]) {
        const metric = nums[0];
        const rArr = groupAggregate(allRows, "Region", metric);
        if (rArr.length >= 2) {
          ins.push(`• ${metric} by Region: ${rArr[0].k} tops ${rArr[1].k} (${formatValue(rArr[0].v, cfg.yAxisFormat, metric)} vs ${formatValue(rArr[1].v, cfg.yAxisFormat, metric)}).`);
        }
      }

      // 4) Group comparison (if groupCol present + single metric)
      if (cfg.groupCol && nums.length === 1) {
        const m = nums[0];
        const gArr = groupAggregate(allRows, cfg.groupCol, m);
        if (gArr.length >= 2) {
          ins.push(`• ${m} by ${cfg.groupCol}: ${gArr[0].k} tops ${gArr[1].k} (${formatValue(gArr[0].v, cfg.yAxisFormat, m)} vs ${formatValue(gArr[1].v, cfg.yAxisFormat, m)}).`);
        }
      }

      setInsights(ins.slice(0, 6));
    } catch {
      setInsights([]);
    }
  }

  // ---------- One-click redo suggestions ----------
  const redoSuggestions = useMemo(() => {
    const curr = new Set(
      (resolvedConfig?.y_cols && resolvedConfig.y_cols.length)
        ? resolvedConfig.y_cols
        : (resolvedConfig?.y_col ? [resolvedConfig.y_col] : (yCols.length ? yCols : (yCol ? [yCol] : [])))
    );

    // 🔹 NEW: exclude current X axis + any time/date-like columns
    return numericHeaders
      .filter((h) => {
        if (curr.has(h)) return false;
        if (h === xCol) return false;
        if (isTimeCol(rows, h)) return false;
        return true;
      })
      .slice(0, 6);
  }, [numericHeaders, resolvedConfig, yCol, yCols, xCol, rows]);

  const redoWithMetric = async (metric) => {
    if (resolvedConfig?.y_cols?.length) {
      const nextYCols = [metric, ...resolvedConfig.y_cols.slice(1)];
      await handleChartGeneration({ yCols: nextYCols, yCol: "" });
    } else {
      await handleChartGeneration({ yCol: metric, yCols: [] });
    }
  };

  // Reusable premium button style (off-white + dark green text)
  const premiumBtnProps = {
    bg: OFF_WHITE,
    color: BRAND_GREEN,
    border: "1px solid",
    borderColor: "green.200",
    _hover: { bg: "#EFEDE3" },
    _active: { bg: "#E7E4D8" }
  };

  const AxisLabel = ({ children, tip }) => (
    <HStack spacing={2}>
      <FormLabel mb="0">{children}</FormLabel>
      <Tooltip label={tip} hasArrow placement="right" openDelay={200}>
        <Box as="span" aria-label="Axis help" cursor="help">
          <InfoOutlineIcon color="gray.500" />
        </Box>
      </Tooltip>
    </HStack>
  );

  // === Download using direct anchor (no fetch, avoids CORS issues) ===
  const handleDownload = () => {
    if (!chartUrl) return;
    const link = document.createElement("a");
    link.href = chartUrl;
    link.download = `${(resolvedConfig?.chart_title || "shelfkey-chart")
      .replace(/\s+/g, "-")
      .toLowerCase()}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <AppLayout>
      {/* Premium header bar */}
      <Box
        bgGradient="linear(to-r, green.800, green.600)"
        color="white"
        borderRadius="xl"
        px={6}
        py={5}
        mt={6}
        boxShadow="md"
      >
        <HStack>
          <Box>
            <Heading size="md" lineHeight="short">Charts</Heading>
            <Text opacity={0.85} fontSize="sm">
              Upload your data and generate beautiful charts. (Nothing is saved.)
            </Text>
          </Box>
          <Spacer />
          <Menu>
            <MenuButton as={Button} rightIcon={<ChevronDownIcon />} variant="outline" colorScheme="whiteAlpha">
              Mode: {mode === "form" ? "Form" : "Prompt"}
            </MenuButton>
            <MenuList bg="white" color="gray.800" border="1px solid" borderColor="gray.200" boxShadow="lg">
              <MenuItem color="gray.800" _hover={{ bg: "gray.50" }} onClick={() => setMode("form")}>
                Form
              </MenuItem>
              <MenuItem color="gray.800" _hover={{ bg: "gray.50" }} onClick={() => setMode("prompt")}>
                Ask with text
              </MenuItem>
            </MenuList>
          </Menu>
        </HStack>
      </Box>

      <VStack spacing={6} maxW="1000px" mx="auto" mt={8} align="stretch">
        {/* File input (shared) */}
        <Box bg="white" border="1px solid" borderColor="gray.200" borderRadius="xl" p={5} boxShadow="sm">
          <FormControl>
            <FormLabel>Data File (.csv or .xlsx)</FormLabel>
            <Input type="file" accept=".csv,.xlsx" onChange={handleFileChange} bg="white" />
            {headers.length > 0 && (
              <FormHelperText>
                Detected columns:&nbsp;
                {headers.map(h => (
                  <Tag key={h} mr={1} mt={1} colorScheme={numericHeaders.includes(h) ? "green" : "gray"}>
                    {h}
                  </Tag>
                ))}
              </FormHelperText>
            )}
          </FormControl>

          {/* Small preview */}
          {dataPreview.length > 0 && (
            <Box mt={4} border="1px solid" borderColor="gray.200" borderRadius="md" p={3} bg="gray.50">
              <HStack>
                <Text fontWeight="semibold">Preview (first 5 rows)</Text>
                <Badge colorScheme="gray">{rows.length.toLocaleString()} rows detected</Badge>
              </HStack>
              <Table size="sm" variant="striped" mt={2}>
                <Thead>
                  <Tr>{headers.map(h => <Th key={h}>{h}</Th>)}</Tr>
                </Thead>
                <Tbody>
                  {dataPreview.map((row, i) => (
                    <Tr key={i}>
                      {headers.map(h => <Td key={h}>{String(row[h])}</Td>)}
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Box>
          )}
        </Box>

        {/* Controls */}
        <Box bg="white" border="1px solid" borderColor="gray.200" borderRadius="xl" p={5} boxShadow="sm">
          {mode === "form" ? (
            <>
              <SimpleGrid columns={[1, 2]} spacing={4}>
                <FormControl>
                  <FormLabel>Chart Title</FormLabel>
                  <Input value={chartTitle} onChange={(e) => setChartTitle(e.target.value)} />
                </FormControl>

                <FormControl>
                  <FormLabel>Chart Type</FormLabel>
                  <Select value={chartType} onChange={(e) => setChartType(e.target.value)}>
                    {chartTypes.map((type) => (
                      <option key={type} value={type}>
                        {type.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())}
                      </option>
                    ))}
                  </Select>
                </FormControl>

                {/* X Axis */}
                <FormControl>
                  <AxisLabel tip="X Axis = horizontal. Put categories or time here (e.g., Date, Month, Retailer, Region).">
                    X Axis
                  </AxisLabel>
                  <Select placeholder="Select a column" value={xCol} onChange={(e) => setXCol(e.target.value)}>
                    {headers.map(h => <option key={h} value={h}>{h}</option>)}
                  </Select>
                  <FormHelperText color="gray.600">
                    Common choices: Date/Month for time series, or a category (Retailer, Region, SKU).
                  </FormHelperText>
                </FormControl>

                {/* Y Axis (single) */}
                {(chartType === "bar" ||
                  chartType === "horizontal_bar" ||
                  chartType === "line" ||
                  chartType === "grouped_bar" ||
                  chartType === "pie") && (
                  <FormControl>
                    <AxisLabel tip="Y Axis = vertical. Put numeric metrics here (e.g., Sales, Units, ACV%).">
                      Y Axis
                    </AxisLabel>
                    <Select placeholder="Select a column" value={yCol} onChange={(e) => setYCol(e.target.value)}>
                      {headers.map(h => <option key={h} value={h}>{h}</option>)}
                    </Select>
                  </FormControl>
                )}

                {/* Group column */}
                {chartType === "grouped_bar" && (
                  <FormControl>
                    <FormLabel>Group (Series)</FormLabel>
                    <Select placeholder="Select a column" value={groupCol} onChange={(e) => setGroupCol(e.target.value)}>
                      {headers.map(h => <option key={h} value={h}>{h}</option>)}
                    </Select>
                    <FormHelperText color="gray.600">Creates multiple bars/lines per X value.</FormHelperText>
                  </FormControl>
                )}

                {/* Multi Y for stacked/dual-line */}
                {(chartType === "stacked_component_bar" ||
                  chartType === "stacked_component_bar_horizontal" ||
                  chartType === "line_dual") && (
                  <FormControl>
                    <AxisLabel tip="Choose multiple numeric fields (hold Cmd/Ctrl to multi-select).">
                      Y Axes (multiple)
                    </AxisLabel>
                    <Select
                      multiple
                      value={yCols}
                      onChange={(e) => setYCols(Array.from(e.target.selectedOptions).map(o => o.value))}
                      height="120px"
                    >
                      {headers.map(h => <option key={h} value={h}>{h}</option>)}
                    </Select>
                    <FormHelperText>Select two for dual-line, or several for stacked bars.</FormHelperText>
                  </FormControl>
                )}

                <FormControl>
                  <FormLabel>Y-Axis Format</FormLabel>
                  <Select value={yAxisFormat} onChange={(e) => setYAxisFormat(e.target.value)}>
                    <option value="">None</option>
                    <option value="$">Dollar ($)</option>
                    <option value="%">Percent (%)</option>
                  </Select>
                </FormControl>

                <FormControl display="flex" alignItems="center">
                  <FormLabel mb="0">Show Annotations</FormLabel>
                  <Switch isChecked={showAnnotations} onChange={() => setShowAnnotations((p) => !p)} />
                </FormControl>
              </SimpleGrid>

              <HStack mt={3} spacing={3}>
                <Tooltip label="Auto-detect columns" hasArrow>
                  <Button leftIcon={<RepeatIcon />} onClick={() => autoGuess(rows)} bg={OFF_WHITE} color={BRAND_GREEN} border="1px solid" borderColor="green.200" _hover={{ bg: "#EFEDE3" }} _active={{ bg: "#E7E4D8" }}>
                    Auto
                  </Button>
                </Tooltip>
                <Button onClick={() => handleChartGeneration()} bg={OFF_WHITE} color={BRAND_GREEN} border="1px solid" borderColor="green.200" _hover={{ bg: "#EFEDE3" }} _active={{ bg: "#E7E4D8" }}>
                  Generate Chart
                </Button>
              </HStack>
            </>
          ) : (
            <>
              {/*✅ NEW: Chart title for Prompt mode */}
              <FormControl mb={4}>
                <FormLabel>Chart Title (option)</FormLabel>
                <Input
                  placeholder="e.g., Velocity Over Time"
                  value={chartTitle}
                  onChange={(e) => setChartTitle(e.target.value)}
                  bg="white"
                />
              </FormControl>
               
              <FormControl>
                <FormLabel>Describe the chart you want</FormLabel>
                <Textarea
                  placeholder={`e.g., "Line chart of units over time by region. Put ACV on a secondary axis and only show 2024."`}
                  value={promptText}
                  onChange={(e) => setPromptText(e.target.value)}
                  rows={4}
                  bg="white"
                />
              </FormControl>
              <HStack>
                <Button onClick={handlePromptGeneration} bg={OFF_WHITE} color={BRAND_GREEN} border="1px solid" borderColor="green.200" _hover={{ bg: "#EFEDE3" }} _active={{ bg: "#E7E4D8" }}>
                  Generate from Prompt
                </Button>
                <Text color="gray.600" fontSize="sm">
                  Tip: Try “group by retailer”, “only 2024”, “secondary axis”.
                </Text>
              </HStack>
            </>
          )}
        </Box>

        {/* Output */}
        {(loadingChart || chartUrl) && (
          <Box bg="white" border="1px solid" borderColor="gray.200" borderRadius="xl" p={5} boxShadow="sm">
            <HStack justify="space-between" mb={2}>
              <HStack>
                <Text fontWeight="bold">Generated Chart</Text>
                {resolvedConfig?.chart_title ? (
                  <Badge colorScheme="green">{resolvedConfig.chart_title}</Badge>
                ) : null}
              </HStack>
            </HStack>

            <Skeleton isLoaded={!loadingChart} borderRadius="md">
              {chartUrl && (
                <Image src={chartUrl} alt="Chart" borderRadius="md" maxW="100%" />
              )}
            </Skeleton>

            {/* One-click redo suggestions */}
            {chartUrl && redoSuggestions.length > 0 && (
              <Box mt={4}>
                <Text fontWeight="semibold" mb={2}>Try another metric:</Text>
                <HStack wrap="wrap">
                  {redoSuggestions.map(m => (
                    <Button key={m} size="sm" onClick={() => redoWithMetric(m)} bg={OFF_WHITE} color={BRAND_GREEN} border="1px solid" borderColor="green.200" _hover={{ bg: "#EFEDE3" }} _active={{ bg: "#E7E4D8" }}>
                      {m}
                    </Button>
                  ))}
                </HStack>
              </Box>
            )}

            {/* Auto-Insights */}
            {chartUrl && insights.length > 0 && (
              <Box mt={5} p={4} bg="green.50" border="1px solid" borderColor="green.200" borderRadius="md">
                <HStack>
                  <Heading size="sm">Insights</Heading>
                  <Badge colorScheme="green" variant="subtle">Auto-generated</Badge>
                </HStack>
                <VStack align="start" spacing={1} mt={2}>
                  {insights.map((line, idx) => (
                    <Text key={idx} fontSize="sm">{line}</Text>
                  ))}
                </VStack>
              </Box>
            )}

            <Text fontSize="sm" mt={3} color="gray.500">
              Tip: Right-click the chart to save it or take a screenshot.
            </Text>
          </Box>
        )}
      </VStack>
    </AppLayout>
  );
};

export default Charts;
